import { useEffect, useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";

import {
  AccountEltDestinationFragment,
  EltSyncAdditionalDestinationInput,
  IdentifierCasing,
  useEltDestinationsQuery,
} from "@/apollo/types";
import { BetaFeatureText } from "@/components/elements/FeatureStateText";
import { Checkbox } from "@/components/primitives/Checkbox";
import { InputDescription } from "@/components/primitives/InputDescription";
import { Input } from "@/components/primitives/input";
import {
  SkippedFeaturesAlert,
  defaultDestinationSchemaName,
  destinationCasing,
  destinationLabel,
  skippedFeatures,
} from "@/features/elt-destinations";
import { useCurrentPlan } from "@/features/subscription";
import { IntegrationLogo } from "@/integrations";

import { SubHeading } from "../../components/Layout";
import { useDataSourceStateContext } from "../../step-state";

type FormModel = ReturnType<
  typeof useDataSourceStateContext
>["state"]["syncSettings"]["value"];

type StreamSettings = Parameters<typeof skippedFeatures>[1][number];

export function AdditionalDestinationsField(props: {
  streams: StreamSettings[];
  sourceIntegrationId: string;
}) {
  const { features, loading } = useCurrentPlan();
  const enabled = !loading && features.multiDestinationElt;
  const { data } = useEltDestinationsQuery({ fetchPolicy: "network-only" });
  const formContext = useFormContext<FormModel>();
  const { field } = useController({
    name: "additionalDestinations",
    control: formContext.control,
  });
  const destinations = useMemo(
    () => data?.eltDestinations ?? [],
    [data?.eltDestinations],
  );
  const defaults = destinations.filter((d) => d.useForNewSyncs);
  const unchosen = field.value === null;
  const selected = field.value ?? [];

  const { setValue } = formContext;
  useEffect(() => {
    if (enabled && data && unchosen) {
      setValue(
        "additionalDestinations",
        destinations
          .filter((d) => d.useForNewSyncs)
          .map((d) => ({
            eltDestinationId: d.id,
            destinationSchemaName: null,
          })),
        { shouldDirty: false },
      );
    }
  }, [enabled, data, unchosen, destinations, setValue]);

  if (!loading && !enabled) {
    return defaults.length > 0 ? (
      <DefaultsSkipped
        defaults={defaults}
        reason="the account's plan does not include loading a sync into more than one destination"
      />
    ) : null;
  }
  if (!enabled || destinations.length === 0) {
    return null;
  }

  const update = (
    eltDestinationId: string,
    change: Partial<EltSyncAdditionalDestinationInput> | null,
  ) => {
    const rest = selected.filter(
      (d) => d.eltDestinationId !== eltDestinationId,
    );
    const current = selected.find(
      (d) => d.eltDestinationId === eltDestinationId,
    );
    field.onChange(
      change === null
        ? rest
        : [...rest, { ...current, ...change, eltDestinationId }],
    );
  };

  return (
    <div className="mt-8">
      <SubHeading className="mb-1">
        Also load into <BetaFeatureText />
      </SubHeading>
      <InputDescription className="mb-3">
        ELT destinations this sync loads into next to the data warehouse.
      </InputDescription>
      <div className="flex flex-col gap-3">
        {destinations.map((destination) => (
          <DestinationOption
            key={destination.id}
            destination={destination}
            value={selected.find((d) => d.eltDestinationId === destination.id)}
            onChange={(change) => update(destination.id, change)}
            streams={props.streams}
            sourceIntegrationId={props.sourceIntegrationId}
            syncSchemaName={formContext.watch("destinationSchemaName")}
            syncCasing={formContext.watch("identifierCasing")}
          />
        ))}
      </div>
    </div>
  );
}

function DefaultsSkipped(props: {
  defaults: AccountEltDestinationFragment[];
  reason: string;
}) {
  const labels = props.defaults.map(destinationLabel).join(", ");
  return (
    <div className="mt-8">
      <SubHeading className="mb-1">
        Also load into <BetaFeatureText />
      </SubHeading>
      <InputDescription>
        {labels} {props.defaults.length === 1 ? "is" : "are"} used for new
        syncs, but this sync does not load into ELT destinations: {props.reason}
        .
      </InputDescription>
    </div>
  );
}

function DestinationOption(props: {
  destination: AccountEltDestinationFragment;
  value: EltSyncAdditionalDestinationInput | undefined;
  onChange: (change: Partial<EltSyncAdditionalDestinationInput> | null) => void;
  streams: StreamSettings[];
  sourceIntegrationId: string;
  syncSchemaName: string;
  syncCasing: IdentifierCasing | null | undefined;
}) {
  const { destination, value } = props;
  const label = destinationLabel(destination);
  const defaultSchemaName = defaultDestinationSchemaName(
    props.syncSchemaName,
    props.syncCasing,
    destinationCasing(destination),
  );

  return (
    <div className="rounded-sm border p-3 text-sm">
      <label className="flex items-center gap-3">
        <Checkbox
          checked={!!value}
          onChange={(e) =>
            props.onChange(
              e.target.checked ? { destinationSchemaName: null } : null,
            )
          }
        />
        <IntegrationLogo id={destination.integrationId} className="h-5" />
        <span className="font-medium">{label}</span>
      </label>
      {value && (
        <div className="mt-3 flex flex-col gap-3 pl-7">
          <div className="w-1/2">
            <Input
              size="md"
              aria-label={`Schema name in ${label}`}
              placeholder={defaultSchemaName}
              value={value.destinationSchemaName ?? ""}
              pattern="^[A-Za-z0-9_]*$"
              title="Letters, numbers and underscores only."
              onChange={(e) =>
                props.onChange({
                  destinationSchemaName: e.target.value.trim() || null,
                })
              }
            />
            <InputDescription className="mt-1">
              Leave empty to use {defaultSchemaName}.
            </InputDescription>
          </div>
          <SkippedFeaturesAlert
            destinationLabel={label}
            warnings={skippedFeatures(
              destination,
              props.streams,
              props.sourceIntegrationId,
            )}
          />
        </div>
      )}
    </div>
  );
}
